import type { OptionsDocument, EmailTemplate, UserPreferencesDocument } from 'paintscout'
import reduce from 'lodash/reduce'
import uniq from 'lodash/uniq'
import { getFeature } from '../get-features'
import { getBankTransferLabel } from '../../../util/getPayrixCredentials'

export const defaultTemplates = {
  'send-invoice-client': {
    label: 'Send Invoice to Client',
    subject: 'Your Invoice from {{companyName}}',
    body: 'To access your invoice from {{companyName}} please click the link below.\n\nBest regards,\n\n{{companyName}}',
    name: 'send-invoice-client'
  },
  'send-work-order-staff': {
    label: 'Send Work Order to Staff',
    subject: 'Your Job from {{companyName}}',
    body: 'To access your job from {{companyName}} please click the link below.\n\nBest regards,\n\n{{companyName}}',
    name: 'send-work-order-staff'
  },
  'send-quote-client': {
    label: 'Send Quote to Client',
    subject: 'Your Estimate from {{companyName}}',
    body: 'To access your estimate from {{companyName}} please click the link below.\n\nBest regards,\n\n{{companyName}}',
    name: 'send-quote-client'
  },
  'send-quote-staff': {
    label: 'Quote Sent Notification to Staff',
    subject: 'Quote #{{quoteNumber}} has been sent',
    body: 'To view the quote please click the link below.',
    name: 'send-quote-staff',
    enabled: true
  },
  'send-invoice-staff': {
    label: 'Invoice Sent Notification to Staff',
    subject: 'Invoice #{{quoteNumber}} has been sent',
    body: 'To view the invoice please click the link below.',
    name: 'send-invoice-staff'
  },
  'quote-accepted-notification': {
    label: 'Quote Accepted Notification to Staff',
    subject: 'Your Quote has been Accepted',
    body: 'To view the quote, please click the link below.',
    name: 'accept-quote-staff',
    enabled: false
  },
  'accept-thank-you': {
    label: 'Quote Accepted Thank You to Customer',
    subject: 'Thank You for Accepting!',
    body: "Thank you for accepting your quote from {{companyName}}. We'll be in touch!",
    name: 'accept-thank-you',
    enabled: false
  },
  'decline-thank-you': {
    label: 'Quote Declined Email Sent to Client',
    subject: 'You have declined #{{quoteNumber}}!',
    body: 'Hey #{{clientName}}, we see you declined your quote! If you have any questions, please let us know!',
    name: 'decline-thank-you',
    enabled: false
  },
  'quote-declined-notification': {
    label: 'Quote Declined Notification to Staff',
    subject: 'Your Quote has been Declined',
    body: 'To view the quote, please click the link below.',
    name: 'decline-quote-staff',
    enabled: false
  },
  'payment-received': {
    label: 'Payment Received Email Sent to Client',
    subject: 'Your Payment to {{companyName}} has been Received',
    body: 'Hey {{clientName}},\nWe have received your payment. Thank you!\n\nBest regards,\n\n{{companyName}}.',
    name: 'payment-received',
    enabled: true
  },
  'ach-payment-processing': {
    // Copy lives in getAchPaymentProcessingTemplate() (single source) since the label is
    // dynamic — ACH for US, EFT for Canadian companies. Resolved in getEmailTemplates below.
    // off by default (on-for-new / off-for-existing) — new companies seed enabled:true in the
    // options fixtures as a partial {enabled} override so the dynamic ACH/EFT copy is preserved.
    name: 'ach-payment-processing',
    enabled: false
  },
  'payment-refunded': {
    label: 'Payment Refunded Email Sent to Client',
    subject: 'Your Refund from {{companyName}}',
    body: 'Hey {{clientName}},\nA refund has been issued to your original payment method. Please allow a few business days for it to appear on your statement.\n\nBest regards,\n\n{{companyName}}.',
    name: 'payment-refunded',
    // off by default so existing companies don't start emailing customers about refunds without
    // opting in; new companies are seeded enabled:true via the options fixtures (on-for-new).
    enabled: false
  },
  'payment-failed': {
    label: 'Payment Failed Email Sent to Client',
    subject: 'There was a problem with your payment to {{companyName}}',
    body: 'Hey {{clientName}},\nWe were unable to process your recent payment. Please try again or reach out if you have any questions.\n\nBest regards,\n\n{{companyName}}.',
    name: 'payment-failed',
    // off by default (on-for-new / off-for-existing) — new companies are seeded enabled:true via
    // the options fixtures so existing companies don't start emailing customers without opting in.
    enabled: false
  },
  'payment-request': {
    label: 'Payment Request Email Sent to Client',
    subject: 'Payment Request',
    body: 'Hey {{clientName}}, you have a payment request from {{companyName}}.',
    name: 'payment-request'
  },
  'event-confirmation': {
    label: `Event Confirmation Email Sent to Client`,
    subject: `Your {{companyName}} {{eventType}} is scheduled with {{eventAssigneeFullName}}`,
    body: '<p>Hi {{clientFirstName}},</p><p></p><p style="text-align: start">Your {{eventType}} with {{companyName}} is scheduled for {{eventDate}} at {{eventTime}}.</p><p></p><p style="text-align: start">If you have any questions before our appointment, please let me know!</p><p style="text-align: start"></p><p style="text-align: start">Regards,<br>{{eventAssigneeFullName}}</p>',
    name: 'event-confirmation',
    enabled: true
  },
  'event-reminder-1-day': {
    label: `Event Reminder Email Sent to Client (1 Day)`,
    subject: `Your {{companyName}} {{eventType}} with {{eventAssigneeFullName}} is tomorrow!`,
    body: '<p>Hi {{clientFirstName}},</p><p></p><p style="text-align: start">This is a friendly reminder that your {{eventType}} with {{companyName}} is scheduled for {{eventDate}} at {{eventTime}}.</p><p></p><p style="text-align: start">Looking forward to meeting with you about your upcoming project!</p><p style="text-align: start"></p><p style="text-align: start">Regards,<br>{{eventAssigneeFullName}}</p>',
    name: 'event-reminder-1-day',
    enabled: true
  },
  'event-reminder-1-hour': {
    label: `Event Reminder Email Sent to Client (1 Hour)`,
    subject: `Your {{companyName}} {{eventType}} with {{eventAssigneeFullName}} is in one hour!`,
    body: '<p>Hi {{clientFirstName}},</p><p></p><p style="text-align: start">This is a friendly reminder that your {{eventType}} with {{companyName}} is in one hour. Chat soon!</p><p style="text-align: start"></p><p style="text-align: start">Regards,<br>{{eventAssigneeFullName}}</p>',
    name: 'event-reminder-1-hour',
    enabled: true
  },
  'event-rescheduled': {
    label: `Event Reschedule Email Sent to Client`,
    subject: `Your scheduled {{eventType}} with {{companyName}} has been updated`,
    body: '<p>Hi {{clientFirstName}},</p><p></p><p style="text-align: start">Your {{eventType}} with {{companyName}} has been updated:</p><p></p>{{FILL_TARGET}}<p style="text-align: start"></p><p style="text-align: start">Regards,<br>{{eventAssigneeFullName}}</p>',
    name: 'event-rescheduled',
    enabled: true
  },
  'event-cancelled': {
    label: `Event Cancellation Email Sent to Client`,
    subject: 'Your {{companyName}} {{eventType}} with {{eventAssigneeFullName}} has been cancelled',
    body: '<p>Hi {{clientFirstName}},</p><p></p><p style="text-align: start">Your {{eventType}} with {{eventAssigneeFullName}} from {{companyName}} on {{eventDate}} at {{eventTime}} has been cancelled.</p><p style="text-align: start"></p><p style="text-align: start">Regards,<br>{{eventAssigneeFullName}}</p>',
    name: 'event-cancelled',
    enabled: true
  },
  'lead-form-submitted-client': {
    label: 'Lead Form Submission Email Sent to Client',
    subject: 'Your request for an estimate from {{companyName}} has been received',
    body: "<p>Hi {{clientFirstName}},</p><p></p><p>Thanks for reaching out to {{companyName}}! We've received your request for an estimate on your project, and we're looking forward to learning more about it.</p><p></p><p>A member of our team will reach out to you shortly to discuss the project details and schedule an appointment.</p><p></p><p>If you have any questions in the meantime, feel free to reply directly to this email or call us at {{companyPhone}}.</p><p></p><p>We look forward to working with you!</p><p></p><p>Best,</p><p>{{companyName}}</p>",
    name: 'lead-form-submitted-client',
    // Off for every company — existing and new. Homeowners have not previously received anything
    // from a lead form submission, so this must be opted into from Settings › Emails rather than
    // seeded enabled:true in the options fixtures the way the payment-* templates are.
    enabled: false
  }
}

/**
 * Single source of the ACH/EFT payment-processing email copy. Canadian companies call bank
 * transfers EFT instead of ACH, so the label is parameterized rather than baked into a fixed default.
 */
export function getAchPaymentProcessingTemplate(label: 'ACH' | 'EFT' = 'ACH'): EmailTemplate {
  return {
    label: `${label} Payment Processing Email Sent to Client`,
    subject: `Your ${label} Payment to {{companyName}} is Being Processed`,
    body: `Hey {{clientName}},\nWe have received your ${label}/bank transfer payment. Please allow 3-5 business days for the transfer to complete. You will receive a confirmation once the payment has been processed.\n\nBest regards,\n\n{{companyName}}.`,
    name: 'ach-payment-processing',
    // off-for-existing; new companies are seeded enabled:true via the options fixtures
    enabled: false
  }
}

export function getEmailTemplates({
  options,
  userPreferences
}: {
  options?: OptionsDocument
  userPreferences?: UserPreferencesDocument
}): Record<keyof typeof defaultTemplates, EmailTemplate> {
  const optionEmails = options ? options.options?.emails?.values ?? {} : {}
  const userEmails = userPreferences ? userPreferences.emails?.templates?.values ?? {} : {}
  const hasProfileCommunications = !getFeature({
    options,
    preferences: userPreferences,
    path: 'settingsCommunications.enabled'
  })

  // Canadian companies call bank transfers EFT instead of ACH. Bake the right label into the
  // ach-payment-processing default so it shows correctly before a company customizes the template.
  const bankTransferLabel = options ? getBankTransferLabel(options) : 'ACH'
  const resolvedDefaults = {
    ...defaultTemplates,
    'ach-payment-processing': getAchPaymentProcessingTemplate(bankTransferLabel)
  }

  // Collect all known template keys across all sources
  const allEmailKeys = new Set([
    ...Object.keys(resolvedDefaults),
    ...Object.keys(optionEmails),
    ...(hasProfileCommunications ? Object.keys(userEmails) : [])
  ])

  // Deep merge per template key so a stored partial template (e.g. { enabled: true })
  // does not wipe out required fields (name, subject, body) from the default.
  // Priority (lowest → highest): defaultTemplates → optionEmails → userEmails
  const emails: Record<string, EmailTemplate> = {}
  allEmailKeys.forEach((key) => {
    emails[key] = {
      ...(resolvedDefaults[key] ?? {}),
      ...(optionEmails[key] ?? {}),
      ...(hasProfileCommunications && userEmails[key] ? userEmails[key] : {})
    } as EmailTemplate
  })

  // merge email additionalRecipient values
  const emailsWithMergedRecipients = reduce(
    emails,
    (result, email, key) => {
      const optionRecipients = optionEmails[key] ? optionEmails[key].additionalRecipients ?? [] : []
      const userRecipients = userEmails[key] ? userEmails[key].additionalRecipients ?? [] : []

      return {
        ...result,
        [key]: {
          ...email,
          additionalRecipients: uniq([...(hasProfileCommunications ? userRecipients : []), ...optionRecipients])
        }
      }
    },
    { ...emails }
  )

  return emailsWithMergedRecipients as Record<keyof typeof defaultTemplates, EmailTemplate>
}
